import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CreateOnboardingView.css';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setModalCreateOnboarding, fetchEmptyOnboarding } from './store/onboardingSlice';
import { selectShowCreateModal, selectEmptyOnboarding } from './store/onboardingSlice';
import {
  setNewOnboarding,
  setIsSubsequentDSO,
  setOrganizationId,
  setManualPracticeId,
  resetForm,
  fetchExistingPractices,
  createNewOnboarding,
  selectNewOnboarding,
  selectIsSubsequentDSO,
  selectOrganizationId,
  selectManualPracticeId,
  selectLoading,
  selectLoadingPractices,
  selectError,
  selectIsOrganizationIdValid,
  selectSortedAndHighlightedPracticeIds
} from './store/createOnboardingSlice';

const CreateOnboardingView = () => {
  const dispatch = useAppDispatch();
  const showModal = useAppSelector(state => selectShowCreateModal(state));
  const emptyOnboarding = useAppSelector(state => selectEmptyOnboarding(state));

  // Redux state
  const newOnboarding = useAppSelector(selectNewOnboarding);
  const isSubsequentDSO = useAppSelector(selectIsSubsequentDSO);
  const organizationId = useAppSelector(selectOrganizationId);
  const manualPracticeId = useAppSelector(selectManualPracticeId);
  const loadingPractices = useAppSelector(selectLoadingPractices);
  const error = useAppSelector(selectError);
  const loading = useAppSelector(selectLoading);
  const isOrganizationIdValid = useAppSelector(selectIsOrganizationIdValid);
  const sortedPracticeIds = useAppSelector(selectSortedAndHighlightedPracticeIds);

  // Handle click outside modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      dispatch(setModalCreateOnboarding());
    }
  };

  useEffect(() => {
    dispatch(fetchEmptyOnboarding());
  }, [dispatch]);

  useEffect(() => {
    if (emptyOnboarding) {
      dispatch(setNewOnboarding(emptyOnboarding));
    }
  }, [emptyOnboarding, dispatch]);

  // Handle ESC key
  useEffect(() => {
    if (showModal) {
      const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
          dispatch(setModalCreateOnboarding());
        }
      };
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showModal, dispatch]);

  // Fetch existing practice IDs when organization ID is valid
  useEffect(() => {
    if (isOrganizationIdValid) {
      dispatch(fetchExistingPractices(organizationId));
    }
  }, [organizationId, isOrganizationIdValid, dispatch]);


  // Create new onboarding
  const handleCreateOnboarding = async () => {
    try {
      await dispatch(createNewOnboarding({
        newOnboarding,
        isSubsequentDSO,
        organizationId,
        manualPracticeId
      })).unwrap();
      
      // Close modal and reset form on success
      dispatch(setModalCreateOnboarding());
    } catch (err) {
      console.error('Error creating onboarding:', err);
      // Error is handled by the asyncThunk and displayed via Redux state
    }
  };

  const handleCancel = () => {
    dispatch(setModalCreateOnboarding());
    dispatch(resetForm());
  };

  if (!showModal) {
    return null;
  }

  const mountNode = document.getElementById('root') || document.body;

  return createPortal(
    <div 
      aria-modal="true" 
      role="dialog"
      className={`create-onboarding-modal-overlay ${showModal ? "" : "hidden"}`}
      onClick={handleOverlayClick}
    >
      <div className="create-onboarding-modal-container">
        {/* Header */}
        <div className="create-onboarding-modal-header">
          <h1 className="create-onboarding-modal-title">Create New Onboarding</h1>
        </div>
        
        <div className="create-onboarding-modal-content">
          {loading && (
            <div className="create-onboarding-loading">
              <div>Creating onboarding...</div>
            </div>
          )}
          
          {error && (
            <div className="create-onboarding-error">
              {error}
            </div>
          )}
          
          {!loading && (
            <form className="create-onboarding-form" onSubmit={(e) => { e.preventDefault(); handleCreateOnboarding(); }}>
              {/* Subsequent DSO Practice Checkbox - FIRST */}
              <div className="create-onboarding-form-group">
                <label className="create-onboarding-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isSubsequentDSO}
                    onChange={(e) => dispatch(setIsSubsequentDSO(e.target.checked))}
                    className="create-onboarding-checkbox"
                  />
                  <span className="create-onboarding-checkbox-text">
                    Subsequent DSO practice
                  </span>
                </label>
              </div>

              {/* Organization ID Field - SECOND */}
              {isSubsequentDSO && (
                <div className="create-onboarding-form-group">
                  <label className="create-onboarding-label">
                    Organization ID *
                  </label>
                  <input
                    type="text"
                    value={organizationId}
                    onChange={(e) => dispatch(setOrganizationId(e.target.value))}
                    className={`create-onboarding-input ${organizationId && !isOrganizationIdValid ? 'create-onboarding-input-error' : ''}`}
                    placeholder="Enter organization ID (e.g., org-1747667900140)"
                    required={isSubsequentDSO}
                  />
                  {organizationId && !isOrganizationIdValid && (
                    <p className="create-onboarding-error-message">
                      Organization ID must be in format: org-1747667900140
                    </p>
                  )}
                  {isOrganizationIdValid && (
                    <p className="create-onboarding-success-message">
                      ✓ Valid organization ID format
                    </p>
                  )}
                  
                  {/* Manual Fetch Button */}
                  {isOrganizationIdValid && (
                    <button
                      type="button"
                      onClick={() => dispatch(fetchExistingPractices(organizationId))}
                      disabled={loadingPractices}
                      className="create-onboarding-fetch-button"
                    >
                      {loadingPractices ? 'Fetching...' : 'Fetch Existing Practices'}
                    </button>
                  )}
                </div>
              )}

              {/* Existing Practice IDs - THIRD */}
              {isSubsequentDSO && isOrganizationIdValid && (
                <div className="create-onboarding-form-group">
                  <label className="create-onboarding-label">
                    Existing Practice IDs
                  </label>
                  <div className="create-onboarding-practice-ids-container">
                    {loadingPractices ? (
                      <p className="create-onboarding-practice-ids-loading">Loading existing practices...</p>
                    ) : sortedPracticeIds.length > 0 ? (
                      <div className="create-onboarding-practice-ids-list">
                        {sortedPracticeIds.map((practice, index) => (
                          <div 
                            key={index} 
                            className={`create-onboarding-practice-id-item ${
                              practice.isContiguous 
                                ? 'create-onboarding-practice-id-item-contiguous' 
                                : 'create-onboarding-practice-id-item-normal'
                            }`}
                          >
                            {practice.practiceId}
                            {practice.isContiguous && (
                              <span className="create-onboarding-practice-id-contiguous-label">(contiguous)</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="create-onboarding-practice-ids-empty">No existing practices found for this organization.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Manual Practice ID Field - FOURTH */}
              {isSubsequentDSO && isOrganizationIdValid && (
                <div className="create-onboarding-form-group">
                  <label className="create-onboarding-label">
                    Practice ID *
                  </label>
                  <input
                    type="text"
                    value={manualPracticeId}
                    onChange={(e) => dispatch(setManualPracticeId(e.target.value))}
                    className="create-onboarding-input"
                    placeholder="Enter practice ID (e.g., practice-123)"
                    required={isSubsequentDSO}
                  />
                  <p className="create-onboarding-help-text">
                    This practice ID will be used for the new practice, knowledge base, and onboarding.
                  </p>
                </div>
              )}

              {/* Basic Onboarding Fields */}
              <div className="create-onboarding-form-group">
                <label className="create-onboarding-label">
                  Name *
                </label>
                <input
                  type="text"
                  value={newOnboarding.name}
                  onChange={(e) => dispatch(setNewOnboarding({...newOnboarding, name: e.target.value}))}
                  className="create-onboarding-input"
                  required
                />
              </div>
              
              <div className="create-onboarding-buttons">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="create-onboarding-button create-onboarding-button-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newOnboarding.name || (isSubsequentDSO && (!isOrganizationIdValid || !manualPracticeId.trim())) || loading}
                  className="create-onboarding-button create-onboarding-button-create"
                >
                  {loading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    mountNode
  );
};

export { CreateOnboardingView };
